import Image from 'components/Basic/Image/Image';
import WatchDog from 'components/Blocks/Product/WatchDog/WatchDog';
import Button from 'components/Forms/Button/Button';
import { isB2bDomain, isB2cDomain, isWebDomain } from 'helpers/isDomainLocale';
import { useFormatPrice } from 'hooks/formatting/useFormatPrice';
import { useDomainConfig } from 'hooks/useDomainConfig';
import { AddToCartBasketSmallIcon, SpinnerIcon } from 'public/svg/IconsSvg';
import { twJoin } from 'tailwind-merge';
import { AvailabilityStatusEnum } from 'types/availability';
import { ProductDetailInterfaceType, VariantDetailType } from 'types/product';

type ProductDetailStickyBarProps = {
    isSticky: boolean;
    product: ProductDetailInterfaceType | VariantDetailType;
    onAddToCart: () => void;
    isAddToCartFetching: boolean;
};

const ProductDetailStickyBar: FC<ProductDetailStickyBarProps> = ({
    children,
    product,
    isSticky,
    onAddToCart,
    isAddToCartFetching,
}) => {
    const { formatPriceObject: formatPrice } = useFormatPrice();
    const { name, isSellingDenied, price, availability, uuid, images } = product;
    const domainConfig = useDomainConfig();

    const displayAddToCartButton =
        !isSellingDenied &&
        product.maximumOrderableQuantity > 0 &&
        (!isB2bDomain(domainConfig) || (product.b2bMinimumProductOrder ?? 0) <= product.maximumOrderableQuantity) &&
        !isWebDomain(domainConfig);

    return (
        <div
            className={twJoin(
                'flex items-center justify-between overflow-hidden duration-200 ease-defaultTransition lg:flex',
                isSticky ? 'mb-2 mt-2 h-fit min-h-[70px]' : 'h-0 lg:h-[70px]',
            )}
        >
            {children}
            <div
                className={twJoin(
                    'flex w-full items-center text-small leading-6 transition-opacity duration-200 ease-defaultTransition lg:w-[440px]',
                    '[&>button]:mr-auto [&>button]:whitespace-nowrap [&>button]:p-2 [&>button]:font-bold',
                    isSticky ? 'block' : 'hidden',
                )}
            >
                {images.length > 0 && (
                    <div className="mr-2 flex h-16 w-16 flex-shrink-0 items-center justify-center overflow-hidden py-1 px-0 lg:mr-5">
                        <Image src={images[0]} alt={product.name} width="64" height="56" />
                    </div>
                )}
                <span className="mr-2 line-clamp-3">{name}</span>
                {displayAddToCartButton && (
                    <Button type="button" variant="primary" size="small" onClick={onAddToCart}>
                        {isAddToCartFetching ? (
                            <SpinnerIcon className="h-5 w-5" />
                        ) : (
                            <AddToCartBasketSmallIcon className="h-4 w-4" />
                        )}
                        {formatPrice(price)}
                    </Button>
                )}
                {(isSellingDenied || availability.status === AvailabilityStatusEnum.outOfStock) &&
                    isB2cDomain(domainConfig) && <WatchDog productUuid={uuid} productName={name} />}
            </div>
        </div>
    );
};

export default ProductDetailStickyBar;
